import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Menu, Text } from '@fragments';
import { useScreen } from '@hooks/useScreen';
import { IconCarouselHorizontal, IconFileExport, IconFileImport, IconFilePlus, IconLayoutGrid, IconMoon, IconPhoto, IconSun, IconTag, IconWorld } from '@tabler/icons-react';
import ArtEditor, { ArtGallery, TemplateDialog, normalizeDocument, readTemplateFile, saveBlob, templateFileName, updateLayer, writeTemplateFile } from '../../features/ArtEditor';
import StudioBrand from '../../layout/StudioBrand';
import useTemplateDraft from './hooks/useTemplateDraft';
import { blankTemplate, documentUploads, generatorCategories, generatorGallery, layerRoles, localImage, photoLibrary } from '../../data/templateGenerator';
import './styles.scss';

const POST = 'instagram::post';
const CAROUSEL = 'instagram::carousel';
const LOCALES = { pt: 'pt_BR', en: 'en_US', es: 'es_ES' };
const FILE_ERRORS = { json: 'importErrorJson', shape: 'importErrorShape', invalid: 'importErrorInvalid', size: 'importErrorSize' };
// Photos from the computer travel inside the file, so it may outgrow the Studio's 5 MB import.
const MAX_FILE_MB = 25;
const isJson = file => !!file && (file.type === 'application/json' || /\.json$/i.test(file.name));

function useNarrow(query) {
  const [narrow, setNarrow] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const update = () => setNarrow(list.matches);
    list.addEventListener('change', update);
    return () => list.removeEventListener('change', update);
  }, [query]);
  return narrow;
}

// /studio/art-template-generator, public: the art editor alone, to build templates. Nothing
// reaches a server. A template starts blank, from the web's template catalog or from a JSON
// file, keeps its draft in this browser, and leaves as a JSON file ("Exportar JSON") that the
// Studio's "Adicionar modelo" and this page read back. The header also sets each layer's role
// in the template (its binding: what the AI writes there).
export default function ArtTemplateGenerator() {
  const { t, i18n } = useTranslation('studio');
  const { t: te } = useTranslation('artEditor');
  const { resolvedAppearance, setAppearance, setLanguage, language, setSnackbar } = useScreen();
  const locale = LOCALES[(i18n.language || 'pt').slice(0, 2)] || 'pt_BR';
  const narrow = useNarrow('(max-width: 900px)');
  const { document, meta, latest, edit, setMeta, replace } = useTemplateDraft({ onStorageFull: () => setSnackbar(t('generatorStorageFull')) });
  const [selection, setSelection] = useState({ pageId: null, layers: [] });
  const [galleryFormat, setGalleryFormat] = useState(null);
  const [exporting, setExporting] = useState(false);
  const input = useRef(null);

  const gallery = useMemo(() => generatorGallery(locale), [locale]);
  const categories = useMemo(() => generatorCategories(locale), [locale]);
  // "Sua galeria": the photos this template already carries, then the photo library.
  const images = useMemo(() => ({
    list: async () => ({ items: [...documentUploads(latest.current.document), ...photoLibrary()], next: null }),
    upload: localImage,
  }), [latest]);
  const formats = [{ id: POST, label: te('formatPost'), available: true }, { id: CAROUSEL, label: te('formatCarousel'), available: true }];
  const formatName = id => te(id === CAROUSEL ? 'formatCarousel' : 'formatPost');

  // Another template in the editor: its previous one comes back with undo.
  const start = useCallback(next => {
    replace(next);
    setSelection({ pageId: null, layers: [] });
  }, [replace]);
  const importFile = useCallback(async file => {
    if (!file) return;
    const result = file.size > MAX_FILE_MB * 1024 * 1024 ? { error: 'size' } : readTemplateFile(await file.text(), { fileName: file.name, language: i18n.language });
    if (result.error) { setSnackbar(te(FILE_ERRORS[result.error], { paths: (result.paths || []).join(', '), size: MAX_FILE_MB })); return; }
    const { document: imported, ...fields } = result.template;
    start({ document: imported, meta: fields });
    setSnackbar(t('generatorImported', { name: fields.name || file.name }));
  }, [i18n.language, setSnackbar, start, t, te]);
  const exportFile = async fields => {
    setMeta({ name: fields.name, description: fields.description, category_key: fields.category_key, tags: fields.tags });
    saveBlob(new Blob([writeTemplateFile(fields)], { type: 'application/json' }), templateFileName(fields.name));
    setExporting(false);
  };

  // The role of the one selected layer, set from the header (an undoable step in the editor).
  const layer = selection.layers.length === 1 ? selection.layers[0] : null;
  const roleName = role => (role ? te(`binding_${role}`) : t('generatorRoleNone'));
  const roles = layer ? layerRoles(layer).map(role => ({ label: roleName(role), active: (layer.binding || null) === role,
    onClick: () => edit(updateLayer(latest.current.document, layer.id, { binding: role })) })) : [];

  const uploads = exporting ? documentUploads(document).length : 0;
  const label = text => (narrow ? undefined : text);
  return <div className="studio-screen art-generator"
    onDragOver={event => { if ([...event.dataTransfer.types].includes('Files')) event.preventDefault(); }}
    onDropCapture={event => {
      // A JSON file dropped anywhere opens it; images keep landing on the art.
      const file = [...(event.dataTransfer.files || [])].find(isJson);
      if (!file) return;
      event.preventDefault();
      event.stopPropagation();
      importFile(file);
    }}>
    <header className="art-generator__header">
      <div className="art-generator__leading">
        <StudioBrand />
        <span className="art-generator__divider" aria-hidden="true" />
        <div className="art-generator__title">
          <Text.caption contrast={6}>{t('generatorTitle')}</Text.caption>
          <Text.label contrast={9} maxLines={1}>{meta.name || t('generatorUntitled')}</Text.label>
        </div>
      </div>
      {layer && <div className="art-generator__role">
        {!narrow && <Text.caption contrast={6}>{t('generatorRole')}</Text.caption>}
        <Menu placement="bottom" items={roles}>
          <Button size="small" variant="rounded" color="overlay" dropdown preventLoading startIcon={<IconTag />} aria-label={`${t('generatorRole')}: ${roleName(layer.binding || null)}`}>
            {roleName(layer.binding || null)}
          </Button>
        </Menu>
      </div>}
      <div className="art-generator__actions">
        <Menu placement="bottom-end" items={[
          { label: t('generatorNewPost'), icon: <IconPhoto />, onClick: () => start(blankTemplate(POST)) },
          { label: t('generatorNewCarousel'), icon: <IconCarouselHorizontal />, onClick: () => start(blankTemplate(CAROUSEL)) },
          { divider: true },
          { label: t('generatorFromTemplate'), icon: <IconLayoutGrid />, onClick: () => setGalleryFormat(document.format === CAROUSEL ? CAROUSEL : POST) },
        ]}>
          <Button size="small" variant="rounded" color="overlay" preventLoading {...(narrow ? { icon: <IconFilePlus /> } : { startIcon: <IconFilePlus />, dropdown: true })}
            aria-label={t('generatorNew')} tooltip={narrow ? t('generatorNew') : undefined}>{label(t('generatorNew'))}</Button>
        </Menu>
        <Button size="small" variant="rounded" color="overlay" preventLoading {...(narrow ? { icon: <IconFileImport /> } : { startIcon: <IconFileImport /> })}
          aria-label={t('generatorImport')} tooltip={t('generatorImportHint')} onClick={() => input.current?.click()}>{label(t('generatorImport'))}</Button>
        <Button size="small" variant="rounded" color="inverse" preventLoading {...(narrow ? { icon: <IconFileExport /> } : { startIcon: <IconFileExport /> })}
          aria-label={t('generatorExport')} tooltip={narrow ? t('generatorExport') : undefined} onClick={() => setExporting(true)}>{label(t('generatorExport'))}</Button>
        <span className="art-generator__divider" aria-hidden="true" />
        <Menu items={['pt', 'en', 'es'].map((code, index) => ({ label: ['Português', 'English', 'Español'][index], active: language === code, onClick: () => setLanguage(code) }))}>
          <Button variant="text" color="overlay" size="small" startIcon={<IconWorld />} aria-label={t('language')} preventLoading>{language?.toUpperCase()}</Button>
        </Menu>
        <Button color="overlay" variant="text" size="small" preventLoading icon={resolvedAppearance === 'dark' ? <IconMoon /> : <IconSun />}
          aria-label={t('theme')} tooltip={t('theme')} onClick={() => setAppearance(resolvedAppearance === 'dark' ? 'light' : 'dark')} />
      </div>
    </header>
    <main className="art-generator__main">
      <ArtEditor value={document} onChange={edit} images={images} gallery={gallery} title={meta.name || t('generatorFileName')} onSelectionChange={setSelection} />
    </main>
    <input ref={input} type="file" accept=".json,application/json" hidden onChange={event => { const [file] = event.target.files; event.target.value = ''; importFile(file); }} />
    {galleryFormat && <ArtGallery open onClose={() => setGalleryFormat(null)} source={gallery} format={galleryFormat} formats={formats} onFormatChange={setGalleryFormat}
      title={t('generatorFromTemplate')} onPick={picked => {
        start({ document: normalizeDocument(picked.document), meta: { name: picked.name, description: picked.description || null, category_key: picked.category_key || null, tags: picked.tags || [] } });
        setGalleryFormat(null);
      }} />}
    {exporting && <TemplateDialog open mode="export" onClose={() => setExporting(false)} document={document} format={document.format} formatName={formatName}
      categories={categories} initial={meta} notice={uploads ? t('generatorLocalImages', { count: uploads }) : null} onSubmit={exportFile} />}
  </div>;
}
